import { App } from "antd"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDateFormatter } from "../../../../../../../../hooks/use-date-formatter.tsx"
import { GanttPdfExportModal } from "./GanttPdfExportModal.tsx"
import { type GanttPdfResult, buildGanttPdfBlob } from "./build-gantt-pdf.tsx"

/** The pages a print surface laid out: `[data-pdf-page]` blocks, each holding
 * one `[data-pdf-left]` panel and one `[data-pdf-chart]` timeline. */
export function printedPagesOf(root: HTMLElement | null) {
  if (!root) return []
  return Array.from(
    root.querySelectorAll<HTMLElement>("[data-pdf-page]")
  ).flatMap((page) => {
    const leftPanelEl = page.querySelector<HTMLElement>("[data-pdf-left]")
    const chartEl = page.querySelector<HTMLElement>("[data-pdf-chart]")
    return leftPanelEl && chartEl ? [{ leftPanelEl, chartEl }] : []
  })
}

/**
 * The on-screen chart is zoomed and only draws the rows in view, so it cannot
 * be photographed. `generate` asks the Gantt to mount a print surface
 * (`printing` turns true, attached to `printRef`), captures its pages once
 * they are in the document, then unmounts it.
 */
export function useGanttPdfExport({
  projectName,
  prepare
}: {
  projectName?: string
  /** Awaited before the print surface mounts, e.g. to load every row. */
  prepare?: () => Promise<void>
}) {
  const { t } = useTranslation()
  const { notification } = App.useApp()
  // @react-pdf/renderer builds its document via `pdf(<.../>)`, a separate
  // renderer instance the app's Redux store never reaches -- the account's
  // date format has to be resolved here, in a real hook, and handed down as
  // an already-formatted string.
  const { formatDate } = useDateFormatter()
  const printRef = useRef<HTMLDivElement>(null)
  const [printing, setPrinting] = useState(false)
  const [preparing, setPreparing] = useState(false)
  const [result, setResult] = useState<GanttPdfResult | null>(null)

  // Read at capture time; the effect below must run once per request, not
  // again whenever one of these gets a new identity.
  const latest = useRef({ projectName, formatDate, notification, t, prepare })
  latest.current = { projectName, formatDate, notification, t, prepare }

  useEffect(() => {
    if (!printing) return
    let cancelled = false
    const capture = async () => {
      const pages = printedPagesOf(printRef.current)
      if (!pages.length) return
      const {
        projectName: name,
        formatDate: format,
        notification: notify,
        t: translate
      } = latest.current
      try {
        const built = await buildGanttPdfBlob({
          pages,
          projectName: name,
          generatedAtLabel: format(new Date()) ?? ""
        })
        if (!cancelled) setResult(built)
      } catch (e) {
        console.error("Generate Gantt PDF error: ", e)
        notify.error({
          message: translate("milestonesGanttPdf.generateFailed")
        })
      }
    }
    void capture().finally(() => {
      if (!cancelled) setPrinting(false)
    })
    return () => {
      cancelled = true
    }
  }, [printing])

  const generate = useCallback(async () => {
    const before = latest.current.prepare
    if (before) {
      setPreparing(true)
      try {
        await before()
      } catch (e) {
        console.error("Prepare Gantt PDF error: ", e)
      } finally {
        setPreparing(false)
      }
    }
    setPrinting(true)
  }, [])
  const closeModal = useCallback(() => setResult(null), [])

  return {
    printRef,
    printing,
    generating: preparing || printing,
    generate,
    modal: (
      <GanttPdfExportModal
        open={!!result}
        blob={result?.blob ?? null}
        scaledDown={!!result?.scaledDown}
        projectName={projectName}
        onClose={closeModal}
      />
    )
  }
}
